import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Package, Search, Filter, RefreshCw, CheckCircle, XCircle,
  Eye, EyeOff, Plus, Pencil, Trash2, Upload, Image as ImageIcon, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', name: 'All Categories' },
  { id: 'breads', name: 'Breads' },
  { id: 'sourdough', name: 'Sourdough' },
  { id: 'cookies', name: 'Cookies' },
  { id: 'tea-cakes', name: 'Tea Cakes' },
  { id: 'cakes', name: 'Cakes' },
  { id: 'sweet-bites', name: 'Sweet Bites' },
  { id: 'savoury-bites', name: 'Savoury Bites' },
  { id: 'hampers', name: 'Gift Hampers' }
];

const AdminProducts = ({ adminToken }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [updating, setUpdating] = useState({});
  
  // Dialog states
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    category: 'breads',
    weight: '',
    in_stock: true,
    image: null,
    imagePreview: ''
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, [adminToken]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/products`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleStockToggle = async (productId, currentStatus) => {
    setUpdating(prev => ({ ...prev, [productId]: true }));
    try {
      await axios.patch(
        `${API}/admin/products/${productId}/stock?in_stock=${!currentStatus}`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, in_stock: !currentStatus } : p
      ));
      
      toast.success(`Product ${!currentStatus ? 'is now in stock' : 'marked as out of stock'}`);
    } catch (error) {
      toast.error('Failed to update stock status');
    } finally {
      setUpdating(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleEditClick = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      description: product.description,
      category: product.category,
      weight: product.weight || '',
      in_stock: product.in_stock !== false,
      image: null,
      imagePreview: product.image || ''
    });
    setShowEditDialog(true);
  };

  const handleAddClick = () => {
    setFormData({
      name: '',
      price: '',
      description: '',
      category: 'breads',
      weight: '',
      in_stock: true,
      image: null,
      imagePreview: ''
    });
    setShowAddDialog(true);
  };

  const handleDeleteClick = (product) => {
    setSelectedProduct(product);
    setShowDeleteDialog(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      setFormData(prev => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleSaveProduct = async () => {
    if (!formData.name || !formData.price || !formData.description || !formData.category) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('price', parseFloat(formData.price));
      data.append('description', formData.description);
      data.append('category', formData.category);
      data.append('weight', formData.weight);
      data.append('in_stock', formData.in_stock);
      
      if (formData.image) {
        data.append('image', formData.image);
      }

      const response = await axios.put(
        `${API}/admin/products/${selectedProduct.id}`,
        data,
        { 
          headers: { 
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      // Update local state
      setProducts(prev => prev.map(p => 
        p.id === selectedProduct.id ? response.data.product : p
      ));
      
      toast.success('Product updated successfully');
      setShowEditDialog(false);
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error(error.response?.data?.detail || 'Failed to update product');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProduct = async () => {
    if (!formData.name || !formData.price || !formData.description || !formData.category) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (!formData.image) {
      toast.error('Please select an image for the product');
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('price', parseFloat(formData.price));
      data.append('description', formData.description);
      data.append('category', formData.category);
      data.append('weight', formData.weight);
      data.append('in_stock', formData.in_stock);
      data.append('image', formData.image);

      const response = await axios.post(
        `${API}/admin/products`,
        data,
        { 
          headers: { 
            Authorization: `Bearer ${adminToken}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      // Add to local state
      setProducts(prev => [...prev, response.data.product]);
      
      toast.success('Product created successfully');
      setShowAddDialog(false);
    } catch (error) {
      console.error('Failed to create product:', error);
      toast.error(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${API}/admin/products/${selectedProduct.id}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      
      setProducts(prev => prev.filter(p => p.id !== selectedProduct.id));
      toast.success('Product deleted successfully');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Failed to delete product');
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = products.filter(product => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!product.name.toLowerCase().includes(query) && 
          !product.id.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (categoryFilter !== 'all' && product.category !== categoryFilter) {
      return false;
    }
    if (stockFilter === 'in_stock' && product.in_stock === false) {
      return false;
    }
    if (stockFilter === 'out_of_stock' && product.in_stock !== false) {
      return false;
    }
    return true;
  });

  const inStockCount = products.filter(p => p.in_stock !== false).length;
  const outOfStockCount = products.filter(p => p.in_stock === false).length;

  // Product Form Component
  const ProductForm = ({ isEdit }) => (
    <div className="space-y-4">
      {/* Image Upload */}
      <div className="space-y-2">
        <Label>Product Image {!isEdit && '*'}</Label>
        <div 
          className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          {formData.imagePreview ? (
            <div className="relative inline-block">
              <img 
                src={formData.imagePreview} 
                alt="Preview" 
                className="max-h-40 rounded-lg mx-auto"
              />
              <Button
                size="icon"
                variant="destructive"
                className="absolute -top-2 -right-2 h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  setFormData(prev => ({ ...prev, image: null, imagePreview: isEdit ? selectedProduct?.image : '' }));
                }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="py-4">
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Click to upload image</p>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG up to 5MB</p>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageChange}
        />
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="name">Product Name *</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter product name"
        />
      </div>

      {/* Price and Weight */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="price">Price (₹) *</Label>
          <Input
            id="price"
            type="number"
            value={formData.price}
            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
            placeholder="0"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="weight">Weight/Size</Label>
          <Input
            id="weight"
            value={formData.weight}
            onChange={(e) => setFormData(prev => ({ ...prev, weight: e.target.value }))}
            placeholder="e.g., 500g, 6 pieces"
          />
        </div>
      </div>

      {/* Category */}
      <div className="space-y-2">
        <Label>Category *</Label>
        <Select 
          value={formData.category} 
          onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.filter(c => c.id !== 'all').map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">Description *</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Enter product description"
          rows={3}
        />
      </div>

      {/* In Stock Toggle */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <div>
          <Label>In Stock</Label>
          <p className="text-xs text-muted-foreground">Product will be visible to customers</p>
        </div>
        <Switch
          checked={formData.in_stock}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, in_stock: checked }))}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold text-accent">{products.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Eye className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">In Stock</p>
              <p className="text-2xl font-bold text-green-600">{inStockCount}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <EyeOff className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Out of Stock</p>
              <p className="text-2xl font-bold text-red-600">{outOfStockCount}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <Button 
            className="w-full h-full bg-primary hover:bg-primary/90 gap-2"
            onClick={handleAddClick}
          >
            <Plus className="h-5 w-5" />
            Add Product
          </Button>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={stockFilter} onValueChange={setStockFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Stock" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stock</SelectItem>
              <SelectItem value="in_stock">In Stock</SelectItem>
              <SelectItem value="out_of_stock">Out of Stock</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchProducts}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* Products List */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">Loading products...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <Card className="p-8 text-center">
          <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold text-accent mb-2">No Products Found</h3>
          <p className="text-muted-foreground mb-4">Try adjusting your filters or add a new product</p>
          <Button onClick={handleAddClick} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Product
          </Button>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredProducts.map((product) => (
            <Card 
              key={product.id} 
              className={`p-4 transition-all ${product.in_stock === false ? 'bg-gray-50 opacity-75' : ''}`}
            >
              <div className="flex items-center gap-4">
                {/* Product Image */}
                <div className="w-16 h-16 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <Package className="h-8 w-8 text-primary" />
                  )}
                </div>
                
                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground font-mono">#{product.id}</span>
                    <h3 className="font-semibold text-accent">{product.name}</h3>
                    {product.in_stock === false && (
                      <Badge variant="outline" className="text-red-600 border-red-200 text-xs">
                        Hidden
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-1 text-sm">
                    <span className="font-bold text-primary">₹{product.price}</span>
                    <Badge variant="outline" className="capitalize text-xs">
                      {product.category?.replace('-', ' ')}
                    </Badge>
                    {product.weight && (
                      <span className="text-muted-foreground text-xs">{product.weight}</span>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 mr-2">
                    {product.in_stock !== false ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-600" />
                    )}
                    <Switch
                      checked={product.in_stock !== false}
                      onCheckedChange={() => handleStockToggle(product.id, product.in_stock !== false)}
                      disabled={updating[product.id]}
                      className="data-[state=checked]:bg-green-600"
                    />
                  </div>
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={() => handleEditClick(product)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDeleteClick(product)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Product Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-primary" />
              Edit Product
            </DialogTitle>
          </DialogHeader>
          <ProductForm isEdit={true} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveProduct} disabled={saving} className="gap-2">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Add New Product
            </DialogTitle>
          </DialogHeader>
          <ProductForm isEdit={false} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProduct} disabled={saving} className="gap-2">
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
              Create Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Product
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              Are you sure you want to delete <strong>{selectedProduct?.name}</strong>? 
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteProduct} 
              disabled={saving}
              className="gap-2"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
              Delete Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
